import { useEffect, useRef, useState } from 'react';
import { useRachelScope } from '../contexts/RachelScope.context';
import { RachelScope } from '../effects/rachelScope';
import useRachelChangesStore from '../store/rachelChanges.store';
import { RACHEL_EFFECT_MS, useRachelEffectStyles } from '../styles/rachelEffects.styles';
import { RachelChangedValueAppearance, UseRachelChangedValueOptions } from './rachelEffects.types';

const CLASS_BY_APPEARANCE: Record<
  RachelChangedValueAppearance,
  'changedText' | 'changedInput' | 'changedField'
> = {
  text: 'changedText',
  input: 'changedInput',
  field: 'changedField',
};

const isRachelChangeActive = (scope: RachelScope | null) =>
  !!scope && useRachelChangesStore.getState().isChangeActive(scope);

export const useRachelChangedValue = (
  value: unknown,
  { appearance = 'text', highlightOnMount = false }: UseRachelChangedValueOptions = {},
) => {
  const scope = useRachelScope();
  const { classes } = useRachelEffectStyles();
  const [isChanged, setIsChanged] = useState(() => highlightOnMount && isRachelChangeActive(scope));
  const previousValueRef = useRef(value);

  useEffect(() => {
    if (Object.is(previousValueRef.current, value)) {
      return;
    }

    previousValueRef.current = value;
    setIsChanged(isRachelChangeActive(scope));
  }, [scope, value]);

  useEffect(() => {
    if (!isChanged) {
      return;
    }

    const timeout = setTimeout(() => setIsChanged(false), RACHEL_EFFECT_MS.valueChange);

    return () => clearTimeout(timeout);
  }, [isChanged]);

  return {
    isChanged,
    className: isChanged ? classes[CLASS_BY_APPEARANCE[appearance]] : undefined,
  };
};
